import React from 'react';
import { clsx } from 'clsx';
import { DeliveryType, SettlementVenue } from '@/types';
import type { CustodianItem } from '@/types';
import { getLiveExchangeRate, getSupportedCurrencies } from '@/services/currency/currency-service';
import { roundFxRate } from '@/services/trades/trade-service';
import { FX_RATE_REQUIRED_MESSAGE } from '@/services/trades/trade-validation';
import { useCustodians } from '@/hooks';
import { useFxRatesStore } from '@/stores';
import { DecimalInput } from '@/components/ui/DecimalInput';
import { formatDecimalString } from '@/utils/decimal-display';
import type { TradeForm, CalculatedValues } from './types';
import { inputClassName, labelClassName, readOnlyInputClassName, isAmendLocked } from './types';
import { RegistryNotice } from './RegistryNotice';
import { registryNoticeIds } from './registry-notice-ids';

// FX Rate is shown and edited at 6 decimals throughout the trade-entry form
// (display, tooltips, regex precision in the input). Used both as the
// DecimalInput precision prop and as the rounding target for the auto-fill
// snapshot, so the stored value matches what the user sees.
const FX_RATE_PRECISION = 6;

const roundLiveFxRate = (from: string, to: string): number | undefined => {
  const rate = getLiveExchangeRate(from, to);
  return rate === undefined ? undefined : roundFxRate(rate);
};

/**
 * The three Settlement Place selects differ only in field, label and the DVP
 * mirror, so they share one component. The registry notice is deliberately not
 * gated on `isSubmitted` — amend passes that as a hard `false`, and the registry
 * state is worth stating there too.
 */
const SettlementPlaceSelect: React.FC<{
  form: TradeForm;
  name: 'securities_settlement_place' | 'cash_settlement_place';
  label: string;
  custodians: CustodianItem[];
  registryEmpty: boolean;
  registryError: boolean;
  isFormDisabled: boolean;
  amendMode?: boolean;
  /** Booked place's name, for a value the active list no longer contains. */
  selectedLabel?: string | null;
  /** DVP mirrors the pick onto the cash leg; the FOP legs stand alone. */
  onPick?: (value: number | undefined) => void;
}> = ({
  form,
  name,
  label,
  custodians,
  registryEmpty,
  registryError,
  isFormDisabled,
  amendMode,
  selectedLabel,
  onPick,
}) => (
  <form.Field name={name}>
    {(field) => {
      const describedBy = amendMode
        ? undefined
        : registryNoticeIds(name, { empty: registryEmpty, error: registryError });
      // The list is fetched is_active=true, so a place booked against a
      // since-deactivated custodian has no option to select and the control
      // would read "Select…" — i.e. "not set" for a trade that has one. Amend
      // locks these fields and never PATCHes them, so this is display only,
      // but it is still the wrong answer. Re-inject the booked row instead
      // (CashPoolField does the same for a pool outside the filtered list).
      const booked = field.state.value;
      const bookedMissing =
        booked !== undefined && booked !== null && !custodians.some((c) => c.id === booked);
      return (
        <>
          <label className={labelClassName} htmlFor={field.name}>
            {label}
          </label>
          <select
            id={field.name}
            name={field.name}
            value={field.state.value ?? ''}
            onChange={(e) => {
              const val = e.target.value ? Number(e.target.value) : undefined;
              field.handleChange(val);
              onPick?.(val);
            }}
            disabled={isFormDisabled || isAmendLocked(amendMode, name)}
            aria-describedby={describedBy}
            className={inputClassName}
          >
            {/* Amend says nothing about the register at all — not in the notice
                below, and not here either: the placeholder sits in the DOM next
                to a booked place shown by name. */}
            <option value="">
              {registryEmpty && !amendMode ? 'No custodians configured' : 'Select…'}
            </option>
            {bookedMissing && (
              <option value={booked}>{selectedLabel || `Custodian #${booked}`}</option>
            )}
            {custodians.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
          {/* Not in amend: the field is locked and never PATCHed, so "Add one in
              Registers" is a call to action the trader cannot act on — and with a
              booked place the select shows the custodian's NAME while this line
              would claim the register is empty. */}
          {!amendMode && (
            <RegistryNotice
              registry="custodians"
              idPrefix={name}
              empty={registryEmpty}
              error={registryError}
            />
          )}
        </>
      );
    }}
  </form.Field>
);

interface SettlementDetailsSectionProps {
  form: TradeForm;
  isSubmitted: boolean;
  isFormDisabled: boolean;
  securityCurrency: string;
  effectiveSettlementCurrency: string;
  isCrossCurrency: boolean;
  isCashMatchingMode: boolean;
  isUsingFallbackRates?: boolean;
  deliveryType: string | undefined;
  settlementVenue: string | undefined;
  counterpartyName: string | undefined;
  calculatedValues: CalculatedValues;
  // Inline warning rendered under the FX Rate input. Set by
  // CreateTradeModal whenever the rate is out-of-band (>500 or <0.001).
  // Non-blocking — submission proceeds regardless.
  fxRateWarning?: string;
  /** Booked settlement-place names, so amend can still show a place whose
   *  custodian has since been deactivated (it is absent from the active list). */
  securitiesSettlementPlaceName?: string | null;
  cashSettlementPlaceName?: string | null;
  amendMode?: boolean;
}

export const SettlementDetailsSection: React.FC<SettlementDetailsSectionProps> = ({
  form,
  isSubmitted,
  isFormDisabled,
  securityCurrency,
  effectiveSettlementCurrency,
  isCrossCurrency,
  isCashMatchingMode,
  isUsingFallbackRates,
  deliveryType,
  calculatedValues,
  fxRateWarning,
  securitiesSettlementPlaceName,
  cashSettlementPlaceName,
  amendMode,
}) => {
  const { data: custodians = [], isLoading: custodiansLoading, isError: readFailed } = useCustodians();
  const activeCustodians = React.useMemo(
    () => custodians.filter((c) => c.is_active),
    [custodians],
  );
  // LR-625: an in-flight or failed read also leaves the list empty; saying
  // "none configured" for either is the LR-572 lie. And a failed REFRESH over
  // cached rows sets isError too, so "failed" must mean "failed with nothing to
  // show" or a populated dropdown gets captioned as broken.
  const custodiansError = readFailed && activeCustodians.length === 0;
  const registryEmpty = !custodiansLoading && !readFailed && activeCustodians.length === 0;

  // The auto-fill and "Reset to live FX rate" value; `undefined` when the pair
  // has no live quote, and then the rate must be typed in. Not
  // memoised: live rates sit in a module variable and land after the first
  // render, so a memo keyed on the currencies would keep a stale `undefined`.
  // Subscribing to the FX version re-renders the section when they land.
  useFxRatesStore((s) => s.version);
  const liveFxRate = isCrossCurrency
    ? roundLiveFxRate(securityCurrency, effectiveSettlementCurrency)
    : 1.0;

  // Always include the security currency so a RUB-denominated stock can settle
  // in RUB even when the live FX feed lacks RUB. Without this the <select>
  // would silently fall back to its first <option> (alphabetically AUD), and
  // the submitted payload could carry an unrelated currency.
  const currencyOptions = React.useMemo(
    () => getSupportedCurrencies(securityCurrency ? [securityCurrency] : []),
    [securityCurrency],
  );

  // Amend keeps the booked rate and locks the field, so there is nothing to type in.
  const isMissingCrossFxRate = !amendMode && liveFxRate === undefined;

  // DVP requires both legs to settle in the same place. Steady-state sync
  // (user picks a place while in DVP) is handled inline by the
  // securities_settlement_place onChange below — that setFieldValue is the
  // primary mirror. This effect only catches the FOP→DVP transition: if the
  // user diverged the legs in FOP and then flipped back to DVP, snap cash to
  // the securities value once. Don't remove the inline mirror in favor of
  // this effect — deps don't include the field values, so it won't re-run on
  // ongoing edits.
  React.useEffect(() => {
    if (deliveryType !== 'dvp') return;
    const sec = form.state.values.securities_settlement_place;
    const cash = form.state.values.cash_settlement_place;
    if (sec !== cash) {
      form.setFieldValue('cash_settlement_place', sec);
    }
  }, [deliveryType, form]);
  return (
    <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
      <div>
        <form.Field
          name="delivery_type"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Delivery type is required';
              return undefined;
            },
          }}
        >
          {(field) => (
            <>
              <label className={labelClassName}>
                Delivery Type <span className="text-red-500">*</span>
              </label>
              <select
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value as DeliveryType)}
                disabled={isFormDisabled || isAmendLocked(amendMode, 'delivery_type')}
                className={clsx(inputClassName, isFormDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed')}
              >
                <option value="dvp">DVP</option>
                <option value="fop">FOP</option>
              </select>
              {isSubmitted && field.state.meta.errors.length > 0 && (
                <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
              )}
            </>
          )}
        </form.Field>
      </div>
      <div>
        <form.Field
          name="settlement_venue"
          validators={{
            onChange: ({ value }) => {
              if (!value) return 'Settlement venue is required';
              return undefined;
            },
          }}
        >
          {(field) => (
            <>
              <label className={labelClassName}>
                Settlement Venue <span className="text-red-500">*</span>
              </label>
              <select
                value={field.state.value}
                onChange={(e) => field.handleChange(e.target.value as SettlementVenue)}
                disabled={isFormDisabled || isAmendLocked(amendMode, 'settlement_venue')}
                className={clsx(inputClassName, isFormDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed')}
              >
                <option value="dtc">DTC</option>
                <option value="euroclear">Euroclear</option>
              </select>
              {isSubmitted && field.state.meta.errors.length > 0 && (
                <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
              )}
            </>
          )}
        </form.Field>
      </div>
      <div>
        <form.Field name="settlement_currency">
          {(field) => {
            // Normalize to uppercase before option-list lookup. ISO codes from
            // BO / supported-list / FALLBACK_CURRENCIES are all uppercase, but
            // a defensive normalization protects against any future code path
            // that stores a lowercase value in form state.
            const desired = (field.state.value || securityCurrency || '').toUpperCase();
            // If the desired currency isn't in the option list, render a blank
            // placeholder instead of letting the browser silently pick the
            // first <option>. Form state stays untouched until the user picks.
            const selectValue = currencyOptions.includes(desired) ? desired : '';
            return (
              <>
                <label className={labelClassName} htmlFor="settlement-currency">
                  Settlement Currency
                </label>
                <select
                  id="settlement-currency"
                  value={selectValue}
                  onChange={(e) => {
                    const newCurrency = e.target.value;
                    field.handleChange(newCurrency);
                    if (newCurrency !== securityCurrency) {
                      form.setFieldValue('fx_rate', roundLiveFxRate(securityCurrency, newCurrency));
                    } else {
                      form.setFieldValue('fx_rate', 1.0);
                    }
                    form.setFieldValue('net_settlement_converted_input', undefined);
                  }}
                  disabled={isFormDisabled || isAmendLocked(amendMode, 'settlement_currency')}
                  className={clsx(inputClassName, isFormDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed')}
                >
                  {selectValue === '' && (
                    <option value="" disabled>
                      Select currency…
                    </option>
                  )}
                  {currencyOptions.map((curr) => (
                    <option key={curr} value={curr}>
                      {curr}
                    </option>
                  ))}
                </select>
                {isCrossCurrency && !isMissingCrossFxRate && (
                  <p className="mt-1 text-xs text-yellow-600">
                    Cross-currency settlement: {securityCurrency} → {effectiveSettlementCurrency}. Verify FX rate.
                  </p>
                )}
                {isMissingCrossFxRate && (
                  <p className="mt-1 text-xs text-red-600">
                    No live FX rate for {securityCurrency} → {effectiveSettlementCurrency}. Enter the rate manually.
                  </p>
                )}
              </>
            );
          }}
        </form.Field>
      </div>
      <div>
        <form.Field
          name="fx_rate"
          validators={{
            onChange: ({ value, fieldApi }) => {
              const numValue = typeof value === 'number' ? value : (typeof value === 'string' ? parseFloat(value) : null);
              // 0 is transient while the user types "0.85" through DecimalInput,
              // and submit drops it from the payload like undefined. Without a
              // rate the backend 400s a cross-currency trade. The pair is read
              // from form state: the settlement-currency onChange runs this
              // before the re-render that would refresh `isCrossCurrency`.
              if (numValue === null || numValue === undefined || numValue === 0 || Number.isNaN(numValue)) {
                const settlement = fieldApi.form.getFieldValue('settlement_currency') || securityCurrency;
                return settlement.toUpperCase() !== securityCurrency.toUpperCase()
                  ? FX_RATE_REQUIRED_MESSAGE
                  : undefined;
              }
              if (numValue < 0) return 'FX Rate must be greater than 0';
              // Out-of-band rates (>500 or <0.001) surface a non-blocking
              // inline warning from CreateTradeModal (fxRateWarning prop).
              // Validator stays hard-error-only — no block on submit.
              return undefined;
            },
          }}
        >
          {(field) => {
            const rawValue = field.state.value;
            const numValue = typeof rawValue === 'number' ? rawValue : (typeof rawValue === 'string' ? parseFloat(rawValue) : null);
            const isFxDisabled = isFormDisabled || !isCrossCurrency || isCashMatchingMode;
            // Disabled-mode display value: same currency → 1.0; cash-matching → cash-derived
            // rate from calc, empty until there is a net settlement to derive it from.
            const disabledFxRate = !isCrossCurrency ? 1.0 : calculatedValues.fxRate;
            const disabledDisplay = disabledFxRate === undefined
              ? ''
              : disabledFxRate === 1.0 ? '1.0' : disabledFxRate.toFixed(FX_RATE_PRECISION);
            // Show "Reset to live FX rate" when the field is empty or edited away
            // from the live quote. Right after picking a currency, fx_rate equals
            // the rounded liveFxRate → button hidden.
            const fxDiffersFromLive =
              liveFxRate !== undefined && (rawValue == null || Number(rawValue) !== liveFxRate);
            return (
              <>
                <label className={labelClassName}>
                  {isCashMatchingMode ? 'FX Rate (auto-adjusted)' : 'FX Rate'}
                </label>
                {isFxDisabled ? (
                  <input
                    type="text"
                    value={disabledDisplay}
                    disabled
                    readOnly
                    title={
                      !isCrossCurrency
                        ? 'Same currency - no conversion needed'
                        : disabledFxRate === undefined
                          ? 'Auto-adjusted from settlement cash once Net Settlement is known'
                          : `Auto-adjusted from settlement cash. 1 ${securityCurrency} = ${disabledFxRate.toFixed(FX_RATE_PRECISION)} ${effectiveSettlementCurrency}`
                    }
                    className={clsx(
                      inputClassName,
                      'disabled:bg-gray-100 disabled:cursor-not-allowed',
                      isCashMatchingMode && 'bg-blue-50 border-blue-300',
                      !isCrossCurrency && 'bg-gray-100'
                    )}
                  />
                ) : (
                  <>
                    <DecimalInput
                      value={typeof rawValue === 'number' ? rawValue : undefined}
                      onChange={(v) => {
                        field.handleChange(v);
                        if (v !== undefined) {
                          form.setFieldValue('net_settlement_converted_input', undefined);
                        }
                      }}
                      precision={FX_RATE_PRECISION}
                      // No fallbackValue: an empty field must look empty, since
                      // submit sends only the form's fx_rate. Sub-precision noise
                      // (including "0") clears on blur. Real-world FX
                      // rates like KRW/USD (~0.00072) or IDR/USD (~0.000063)
                      // must be preserved, so the floor is the field's own
                      // precision, not an arbitrary 0.001.
                      minValue={10 ** -FX_RATE_PRECISION}
                      disabled={isAmendLocked(amendMode, 'fx_rate')}
                      title={numValue == null || Number.isNaN(numValue)
                        ? 'Manual entry'
                        : `Manual entry. 1 ${securityCurrency} = ${numValue.toFixed(FX_RATE_PRECISION)} ${effectiveSettlementCurrency}`}
                      className={inputClassName}
                    />
                    {/* LR-484 r1 (L2): fx_rate is a LOCKED condition in amend — hide the
                        reset affordance so a locked field can't be visually mutated
                        (the value isn't sent anyway, but the control must look locked). */}
                    {!amendMode && fxDiffersFromLive && (
                      <div className="mt-1">
                        <button
                          type="button"
                          onClick={() => form.setFieldValue('fx_rate', liveFxRate)}
                          className="text-xs text-gray-900 hover:text-blue-800 underline"
                        >
                          Reset to live FX rate
                        </button>
                      </div>
                    )}
                    {fxRateWarning && (
                      <p className="mt-1 text-xs text-yellow-700">{fxRateWarning}</p>
                    )}
                  </>
                )}
                {isCashMatchingMode && (
                  <p className="mt-1 text-xs text-gray-900">🔄 Adjusted from settlement cash</p>
                )}
                {isUsingFallbackRates && isCrossCurrency && !isCashMatchingMode && !isMissingCrossFxRate && (
                  <p className="mt-1 text-xs text-yellow-600">
                    Using estimated rates. Live rates unavailable.
                  </p>
                )}
                {isSubmitted && field.state.meta.errors.length > 0 && (
                  <p className="mt-1 text-xs text-red-600">{field.state.meta.errors[0]}</p>
                )}
              </>
            );
          }}
        </form.Field>
      </div>
      {deliveryType === 'fop' ? (
        <>
          <div>
            <SettlementPlaceSelect
              form={form}
              name="securities_settlement_place"
              label="Securities Settlement Place"
              custodians={activeCustodians}
              selectedLabel={securitiesSettlementPlaceName}
              registryEmpty={registryEmpty}
              registryError={custodiansError}
              isFormDisabled={isFormDisabled}
              amendMode={amendMode}
            />
          </div>
          <div>
            <SettlementPlaceSelect
              form={form}
              name="cash_settlement_place"
              label="Cash Settlement Place"
              custodians={activeCustodians}
              selectedLabel={cashSettlementPlaceName}
              registryEmpty={registryEmpty}
              registryError={custodiansError}
              isFormDisabled={isFormDisabled}
              amendMode={amendMode}
            />
          </div>
        </>
      ) : (
        <div>
          <SettlementPlaceSelect
            form={form}
            name="securities_settlement_place"
            label="Settlement Place"
            custodians={activeCustodians}
            selectedLabel={securitiesSettlementPlaceName}
            registryEmpty={registryEmpty}
            registryError={custodiansError}
            isFormDisabled={isFormDisabled}
            amendMode={amendMode}
            // DVP: both legs settle at the same place — mirror the value.
            onPick={(val) => form.setFieldValue('cash_settlement_place', val)}
          />
        </div>
      )}
      <div>
        <label className={labelClassName}>
          Margin Price ({securityCurrency})
        </label>
        <input
          type="text"
          value={calculatedValues.marginPrice.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          readOnly
          className={clsx(readOnlyInputClassName, 'text-gray-600')}
        />
      </div>
      <div>
        <label className={labelClassName}>
          Net Settlement ({securityCurrency})
        </label>
        <input
          type="text"
          value={calculatedValues.netSettlement.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
          readOnly
          className={clsx(readOnlyInputClassName, 'text-gray-900 font-semibold')}
        />
      </div>
      {isCrossCurrency && (
        <div>
          <form.Field name="net_settlement_converted_input">
            {(field) => (
              <>
                <label className={labelClassName}>
                  Net Settlement ({effectiveSettlementCurrency}) {isCashMatchingMode ? '💰' : ''}
                </label>
                <DecimalInput
                  value={typeof field.state.value === 'number' ? field.state.value : undefined}
                  onChange={(v) => field.handleChange(v)}
                  precision={2}
                  fallbackValue={calculatedValues.netSettlementConverted}
                  // Focus alone must NOT commit the calculated fallback —
                  // doing so would flip the form into cash-matching mode
                  // (isCashMatchingMode is derived from this field being
                  // populated). Only an actual edit should switch modes.
                  commitFallbackOnFocus={false}
                  // Sub-cent input (incl. "0") clears on blur so we never
                  // park the form in cash-matching mode with a value the
                  // calc treats as "not set" (isCashMatchingMode requires
                  // strictly > 0 — see useCreateTradeForm.ts).
                  minValue={0.01}
                  disabled={isFormDisabled || isAmendLocked(amendMode, 'net_settlement_converted_input')}
                  title={
                    isCashMatchingMode
                      ? 'Cash matching mode: FX rate will be adjusted to match this amount'
                      : 'Editable - enter amount to match counterparty\'s cash'
                  }
                  className={clsx(
                    inputClassName,
                    isFormDisabled && 'disabled:bg-gray-100 disabled:cursor-not-allowed',
                    isCashMatchingMode && 'bg-purple-50 border-purple-300 text-purple-600 font-semibold',
                    !isCashMatchingMode && 'text-purple-600 font-semibold'
                  )}
                />
                {isCashMatchingMode && (
                  <div className="mt-1">
                    {typeof field.state.value === 'number'
                      && calculatedValues.netSettlementConverted !== undefined
                      && calculatedValues.netSettlementConverted !== field.state.value && (
                      <p className="text-xs text-yellow-700">
                        Books as{' '}
                        {formatDecimalString(calculatedValues.netSettlementConverted, {
                          currency: effectiveSettlementCurrency,
                          dp: 2,
                        })}
                        : the FX rate is kept to 6 decimals
                      </p>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        field.handleChange(undefined);
                        form.setFieldValue('fx_rate', liveFxRate);
                      }}
                      className="text-xs text-gray-900 hover:text-blue-800 underline"
                    >
                      {liveFxRate === undefined ? 'Clear' : 'Clear & use live FX rate'}
                    </button>
                  </div>
                )}
              </>
            )}
          </form.Field>
        </div>
      )}
    </div>
  );
};
